import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const FeatureContext = createContext({
  features: {},
  loading: true,
  isFeatureEnabled: () => true,
  getFeatureMessage: () => null,
  refresh: () => {}
});

export const useFeatures = () => {
  const context = useContext(FeatureContext);
  if (!context) {
    throw new Error('useFeatures must be used within FeatureProvider');
  }
  return context;
};

export const FeatureProvider = ({ children }) => {
  const [features, setFeatures] = useState({});
  const [loading, setLoading] = useState(true);
  const API_URL = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    fetchFeatureStatus();
    
    // Refresh feature status every 30 seconds
    const interval = setInterval(fetchFeatureStatus, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchFeatureStatus = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/public/feature-status`);
      setFeatures(response.data || {});
    } catch (error) {
      console.error('Failed to fetch feature status:', error);
      // On error, assume all features are available (fail open)
      setFeatures({});
    } finally {
      setLoading(false);
    }
  };

  const isFeatureEnabled = (featureId) => {
    if (loading) return true; // During loading, don't block UI
    if (!features[featureId]) return true; // Unknown features are enabled
    return features[featureId].available === true;
  };

  const getFeatureMessage = (featureId) => {
    if (!features[featureId]) return null;
    return features[featureId].message || null;
  };

  return (
    <FeatureContext.Provider value={{ features, loading, isFeatureEnabled, getFeatureMessage, refresh: fetchFeatureStatus }}>
      {children}
    </FeatureContext.Provider>
  );
};
